import React, { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import ForbiddenPage from '../pages/ForbiddenPage';

/**
 * Route guard.
 *
 * `requiredPermission` is the RBAC gate and takes a backend permission string
 * such as 'payroll.access_payroll'. Guarding on a capability rather than a
 * role name means adding a role later never requires editing a route.
 *
 * `requireAdmin` remains for the handful of screens that are not modules —
 * user administration — where the backend still checks is_staff.
 *
 * Both are convenience. The API enforces the same rules and is the guarantee;
 * a guard here only spares the user a screen they cannot use.
 */
const PrivateRoute = ({ children, requireAdmin = false, requiredPermission = null }) => {
  const { isAuthenticated, isAdmin, loading, can } = useContext(AuthContext);

  if (loading) return <div>Loading...</div>;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Lacking access is not the same as lacking a session. Sending a signed-in
  // user to /login asks them to sign in again and reads as though their
  // session broke; showing the refusal in place keeps the URL they tried
  // visible, so they can see what was denied.
  if (requireAdmin && !isAdmin) {
    return <ForbiddenPage />;
  }

  if (requiredPermission && !(can && can(requiredPermission))) {
    return <ForbiddenPage />;
  }

  return children;
};

export default PrivateRoute;
